import { useEffect, useRef } from "react";
import { NextRouter, useRouter } from "next/router";
import { stringify } from "query-string";
import { isEqual } from "lodash-es";
import { ParsedUrlQuery } from "querystring";

/**
 * Hook for page params, which are also saved to route query
 */
export function usePageParams(
  initialPageParams: Record<string, string | string[]>
): [ParsedUrlQuery, (pageParams: Record<string, string | string[]>) => void] {
  const router = useRouter();
  const routerRef = useRef<NextRouter>();
  const queryRef = useRef<ParsedUrlQuery>();
  const initialPageParamsRef = useRef<Record<string, string | string[]>>();

  const { query } = router;

  initialPageParamsRef.current = initialPageParams;
  routerRef.current = router;
  queryRef.current = query;

  /** Read initial page params from queryparams */
  useEffect(() => {
    const { pathname, asPath } = routerRef.current as NextRouter;
    const mergedQuery = {
      ...initialPageParamsRef.current,
      ...queryRef.current,
    };
    const stringQuery = stringify(mergedQuery);
    // We need to get rid of searchParams there
    const asPathName = asPath?.split("?")[0] || "";

    (routerRef.current as NextRouter).replace(
      { pathname, query: stringQuery },
      { pathname: asPathName, query: stringQuery },
      {
        shallow: true,
      }
    );
  }, []);

  const setPageParams = (pageParams: Record<string, string | string[]>) => {
    const mergedQuery = { ...query, ...pageParams };
    if (!isEqual(query, mergedQuery)) {
      const { pathname, asPath } = router;
      const stringQuery = stringify(mergedQuery);
      // We need to get rid of searchParams there
      const asPathName = asPath?.split("?")[0] || "";

      router.push(
        { pathname, query: stringQuery },
        { pathname: asPathName, query: stringQuery },
        {
          shallow: true,
        }
      );
    }
  };

  return [query, setPageParams];
}
